import { useRequiredContext } from "@redotech/react-util/context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { parseEmailSignature } from "@redotech/redo-merchant-support-app/client/support";
import Quill from "quill";
import { useEffect, useMemo, useRef, useState } from "react";

export interface CsatSurveyMessageEditor {
  /** Ref callback for the Quill editor the default template renders into. */
  setQuill: (quill: Quill | null) => void;
  /** Mirrors editor content into the form so the save path sees the edits. */
  handleTextChange: () => void;
  /**
   * The message to persist, round-tripped through the signature parser so what
   * is stored matches what the renderer emits. Falls back to the form value
   * only when the parse comes back unusable.
   */
  resolveSurveyMessage: (fallback: string) => Promise<string>;
}

/**
 * Owns the rich-text editor behind the default survey message. Lives outside
 * the Templates panel because the save path needs the editor's content even
 * when that page was never opened.
 */
export function useCsatSurveyMessageEditor({
  bodyContent,
  setBodyContent,
}: {
  /** Seeds a remounting editor, so closing and reopening keeps unsaved edits. */
  bodyContent: string;
  setBodyContent: (html: string) => void;
}): CsatSurveyMessageEditor {
  const client = useRequiredContext(RedoMerchantClientContext);
  const [quill, setQuill] = useState<Quill | null>(null);
  /* Read through a ref so seeding runs once per mount; keying the effect on
     the html itself would re-paste on every keystroke. */
  const bodyContentRef = useRef(bodyContent);
  bodyContentRef.current = bodyContent;

  useEffect(() => {
    if (!quill) {
      return;
    }
    quill.clipboard.dangerouslyPasteHTML(bodyContentRef.current);
  }, [quill]);

  /* Stable identity: this flows into context and `usePageActions` deps. */
  return useMemo(
    () => ({
      setQuill,
      handleTextChange: () => {
        if (quill?.getSemanticHTML) {
          setBodyContent(quill.getSemanticHTML());
        }
      },
      resolveSurveyMessage: async (fallback: string) => {
        /* The editor lives in a modal, so it is usually unmounted at save
           time. Parse from the form value then — only the re-normalisation
           afterwards needs a live editor. */
        const html = quill?.getSemanticHTML() ?? fallback;
        if (!html) {
          return fallback;
        }
        const parsed = await parseEmailSignature(client, { html });
        if (!parsed?.startsWith("<p>")) {
          return fallback;
        }
        if (!quill) {
          return parsed;
        }
        await quill.clipboard.dangerouslyPasteHTML(parsed);
        return quill.getSemanticHTML();
      },
    }),
    [quill, client, setBodyContent],
  );
}
